import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useT } from '@/i18n';
import { api } from '@/api/client';
import type { ReportSummary } from '@/api/types';
import { AppShell } from '@/components/layout';
import { Alert, Button, EmptyState, Skeleton } from '@/components/ui';
import { cx } from '@/components/ui/cx';
import { sceneImage } from '@/lib/illustrations';
import { ROOM_ORDER, type RoomKey } from '@/lib/rooms';
import { ActivityCard, StickerWeek, splitTitle } from '@/features/programme/components';
import { isActive, latestUnlocked, useProgramme } from '@/features/programme/useProgramme';

/** `/programme` → the newest unlocked programme, or a calm explanation when there is none yet. */
export const ProgrammeIndex = () => {
  const t = useT();
  const [reports, setReports] = useState<ReportSummary[]>();
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    api.reports.list().then((r) => setReports(r.reports), () => setFailed(true));
  }, []);

  const latest = reports ? latestUnlocked(reports) : undefined;
  if (latest) return <Navigate to={`/programme/${latest.assessmentId}`} replace />;

  return (
    <AppShell>
      {failed ? (
        <Alert tone="warn">{t('prog.loadFailed')}</Alert>
      ) : !reports ? (
        <Skeleton lines={6} />
      ) : (
        <EmptyState
          image={sceneImage('programme-teaser')}
          title={t('prog.none.title')}
          body={t('prog.none.body')}
          action={
            <Button as="a" href="/children">
              {t('home.startCheckin')}
            </Button>
          }
        />
      )}
    </AppShell>
  );
};

export const Programme = () => {
  const t = useT();
  const { assessmentId = '' } = useParams();
  const state = useProgramme(assessmentId);
  const [room, setRoom] = useState<RoomKey | 'all'>('all');
  const [reportId, setReportId] = useState<string>();

  useEffect(() => {
    api.reports.list().then(
      (r) => setReportId(latestUnlocked(r.reports.filter((x) => x.assessmentId === assessmentId))?.reportId),
      () => undefined,
    );
  }, [assessmentId]);

  const week = state.data?.week;
  const activities = week?.activities ?? [];
  const counts = useMemo(() => {
    const c: Partial<Record<RoomKey, number>> = {};
    for (const a of activities) c[a.room] = (c[a.room] ?? 0) + 1;
    return c;
  }, [activities]);
  const shown = room === 'all' ? activities : activities.filter((a) => a.room === room);
  // Weeks 1 and 2 hold the starters only; the rest are listed apart until they join in week 3.
  const coming = week ? shown.filter((a) => !isActive(a, week)) : [];
  const current = shown.filter((a) => !coming.includes(a));
  const phased = week ? activities.some((a) => !isActive(a, week)) : false;
  const child = state.data?.programme.child.name ?? '';

  if (state.loading) {
    return (
      <AppShell>
        <Skeleton lines={8} />
      </AppShell>
    );
  }
  if (state.locked) {
    return (
      <AppShell>
        <EmptyState
          image={sceneImage('programme-teaser')}
          title={t('checkout.title')}
          body={t('prog.locked.body')}
          action={
            <Button as="a" href={`/sessions/${assessmentId}/checkout`} variant="celebrate">
              {t('checkout.title')}
            </Button>
          }
        />
      </AppShell>
    );
  }
  if (state.error || !week || !state.data) {
    return (
      <AppShell>
        <Alert tone="warn">{t('prog.loadFailed')}</Alert>
        <Button variant="secondary" className="mt-4" onClick={state.reload}>
          {t('prog.retry')}
        </Button>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow text-coral-600">{t('prog.weekOf', { n: week.programmeWeek })}</p>
          <h1 className="mt-1 text-h1">{t('prog.title', { child })}</h1>
          <p className="mt-1 text-body text-ink-soft">{t('prog.sub', { n: activities.filter((a) => isActive(a, week)).length })}</p>
        </div>
        {reportId && (
          <Link to={`/reports/${reportId}`} className="inline-flex min-h-tap items-center rounded-md border-[1.5px] border-sage-300 bg-white px-4 text-label font-extrabold text-lagoon-600 hover:border-lagoon-500">
            {t('prog.pdf')}
          </Link>
        )}
      </div>
      {state.data.programme.watermark && (
        <p className="mt-3 inline-block rounded-sm bg-attention-soft px-3 py-1 text-caption text-attention-ink">{t('act.draft')}</p>
      )}

      <div role="group" aria-label={t('prog.rooms')} className="mt-6 flex gap-2 overflow-x-auto pb-1">
        {(['all', ...ROOM_ORDER] as const)
          .filter((r) => r === 'all' || counts[r])
          .map((r) => (
            <button
              key={r}
              type="button"
              aria-pressed={room === r}
              onClick={() => setRoom(r)}
              className={cx(
                'inline-flex min-h-tap shrink-0 items-center gap-2 rounded-md px-4 text-label font-extrabold shadow-1 transition-colors',
                room === r ? 'bg-ink text-white' : 'bg-white text-ink hover:bg-mist',
              )}
            >
              {r === 'all' ? t('room.all') : t(`room.${r}`)}
              <span className={cx('rounded-sm px-1.5 text-caption', room === r ? 'bg-white/20' : 'bg-mist text-ink-muted')}>
                {r === 'all' ? activities.length : counts[r]}
              </span>
            </button>
          ))}
      </div>

      <div className="mt-6 grid gap-6 nav:grid-cols-[1fr_340px]">
        <section aria-labelledby="activities">
          <h2 id="activities" className={phased ? 'mb-3 text-h3' : 'sr-only'}>{t(phased ? 'prog.phase.start' : 'prog.list')}</h2>
          <ul className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
            {current.map((a) => (
              <li key={a.exerciseId} className="flex">
                <ActivityCard
                  activity={a}
                  scene={state.blocks.get(a.exerciseId)?.scene}
                  count={week.perActivity[a.exerciseId] ?? 0}
                  isPick={week.todaysPick === a.exerciseId}
                  to={`/programme/${assessmentId}/${a.exerciseId}`}
                />
              </li>
            ))}
          </ul>
          {coming.length > 0 && (
            <section aria-labelledby="coming" className="mt-8 rounded-lg bg-mist p-5">
              <h2 id="coming" className="text-h3">{t('prog.phase.later')}</h2>
              <p className="mt-1 text-body text-ink-soft">{t('prog.phase.laterHint')}</p>
              <ul className="mt-3 flex flex-col gap-2">
                {coming.map((a) => (
                  <li key={a.exerciseId}>
                    <Link to={`/programme/${assessmentId}/${a.exerciseId}`} className="flex min-h-tap items-center justify-between gap-3 rounded-md bg-white px-4 text-label text-ink-soft hover:text-ink">
                      <span>{splitTitle(a.title)[0]}</span>
                      <span className="shrink-0 text-caption text-ink-muted">{t('act.minutes', { n: a.durationMinutes })}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </section>
        <aside className="flex flex-col gap-5">
          <StickerWeek week={week} title={t('home.stickers', { child })} hint={t('home.stickersHint')} />
          <div className="rounded-lg bg-wood-100 p-5 text-body text-wood-ink">
            <h2 className="text-[18px] text-ink">{t('prog.tip.title')}</h2>
            <p className="mt-1">{t('prog.tip.body')}</p>
          </div>
          {reportId && (
            <div className="rounded-lg bg-white p-5 shadow-1">
              <h2 className="text-[20px]">{t('prog.share.title')}</h2>
              <p className="mt-1 text-body text-ink-muted">{t('prog.share.body')}</p>
              <Link to={`/reports/${reportId}`} className="mt-3 inline-flex min-h-tap items-center text-label font-extrabold text-lagoon-600 underline-offset-4 hover:underline">
                {t('prog.share.cta')} →
              </Link>
            </div>
          )}
        </aside>
      </div>
    </AppShell>
  );
};
